import { motion } from "framer-motion";
import { CHAPTERS, INDUSTRIES, IMAGES } from "../../data/content";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const COMMITMENTS = ["QUALITY", "SAFETY", "INTEGRITY", "ACCOUNTABILITY"];

const AboutCadPattern = ({ testId = "about-cad-pattern", id = "about-cad-grid" }) => (
  <svg
    aria-hidden="true"
    data-testid={testId}
    className="w-full h-full text-[#24589A]"
    viewBox="0 0 1200 900"
    preserveAspectRatio="none"
  >
    <defs>
      <pattern id={id} width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="900" fill={`url(#${id})`} />
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M110 700 H360 V550 H560 V700 H820 V500 H1080" />
      <path d="M150 190 H430 V310 H680 V190 H990" />
      <path d="M120 120 H390 M120 106 V134 M390 106 V134" />
      <path d="M850 790 H1120 M850 776 V804 M1120 776 V804" />
      <circle cx="430" cy="310" r="36" />
      <circle cx="990" cy="190" r="48" />
      <path d="M942 190 H1038 M990 142 V238" />
    </g>
  </svg>
);

const HeroLine = ({ children, delay, className = "" }) => (
  <motion.span initial="hidden" whileInView="show" viewport={VIEWPORT} className={`block overflow-hidden ${className}`}>
    <motion.span
      variants={{
        hidden: { opacity: 0, x: -42 },
        show: { opacity: 1, x: 0, transition: { duration: 0.68, delay, ease: EASE } },
      }}
      className="block"
    >
      {children}
    </motion.span>
  </motion.span>
);

const chapterVariants = {
  hidden: { opacity: 0, y: 25 },
  show: (delay) => ({ opacity: 1, y: 0, transition: { duration: 0.66, delay, ease: EASE } }),
};

const chapterItemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: (delay) => ({ opacity: 1, y: 0, transition: { duration: 0.52, delay, ease: EASE } }),
};

const ChapterCard = ({ chapter, index }) => {
  const delay = 0.8 + index * 0.2;

  return (
    <motion.article
      data-testid={`chapter-${chapter.num}`}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
      variants={chapterVariants}
      custom={delay}
      className="group relative overflow-hidden rounded-[10px] border border-[#E9EEF2] bg-white/[0.72] p-7 md:p-9 shadow-[0_14px_40px_rgba(19,38,58,0.06)] backdrop-blur-sm transition-[background-color,border-color,box-shadow] duration-300 hover:bg-white/[0.88] hover:border-[#F47721]/25 hover:shadow-[0_20px_50px_rgba(19,38,58,0.09)]"
    >
      <span aria-hidden="true" className="absolute left-0 top-8 bottom-8 w-[3px] origin-top scale-y-0 bg-[#F47721] transition-transform duration-300 group-hover:scale-y-100" />
      <div className="grid grid-cols-[64px_1fr] gap-5 md:gap-7">
        <motion.span
          variants={chapterItemVariants}
          custom={delay + 0.05}
          className="font-manrope text-[48px] md:text-[54px] font-bold leading-none tracking-tight text-[#E2E8EE] transition-colors duration-300 group-hover:text-[#F47721]"
        >
          {chapter.num}
        </motion.span>
        <div>
          <motion.p variants={chapterItemVariants} custom={delay + 0.12} className="font-inter text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F47721]">
            {chapter.title}
          </motion.p>
          <div className="relative inline-block transition-transform duration-300 group-hover:translate-x-1">
            <motion.h3
              variants={chapterItemVariants}
              custom={delay + 0.2}
              className="font-manrope font-bold tracking-tight text-[#17232D] text-[24px] md:text-[28px] leading-[1.16] mt-4"
            >
              {chapter.heading}
            </motion.h3>
            {chapter.num === "02" && (
              <motion.span
                aria-hidden="true"
                variants={{
                  hidden: { scaleX: 0, opacity: 0 },
                  show: { scaleX: 1, opacity: 1, transition: { duration: 0.48, delay: delay + 0.38, ease: EASE } },
                }}
                className="absolute -bottom-2 left-0 h-[2px] w-full origin-left bg-[#F47721]"
              />
            )}
          </div>
          <motion.p variants={chapterItemVariants} custom={delay + 0.3} className="font-inter text-[#59636D] text-[15px] md:text-base leading-[1.7] mt-5 max-w-2xl">
            {chapter.body}
          </motion.p>
          {chapter.num === "04" && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="commitment-indicators">
              {COMMITMENTS.map((item, i) => (
                <motion.span
                  key={item}
                  variants={chapterItemVariants}
                  custom={delay + 0.42 + i * 0.05}
                  className="font-inter text-[10px] font-semibold uppercase tracking-[0.16em] text-[#13263A] bg-white/70 border border-[#E9EEF2] rounded-full px-3 py-2"
                >
                  {item}
                </motion.span>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
};

const founderVariants = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, delay: 1.6, ease: EASE } },
};

const founderItemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: (delay) => ({ opacity: 1, y: 0, transition: { duration: 0.58, delay, ease: EASE } }),
};

const FounderMessage = () => (
  <motion.aside
    data-testid="founder-message"
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    variants={founderVariants}
    className="relative overflow-hidden rounded-[12px] border border-[rgba(255,255,255,0.12)] bg-gradient-to-br from-[#13263A] to-[#172A3A] p-8 lg:p-10 text-white shadow-[0_26px_70px_rgba(19,38,58,0.18)] lg:sticky lg:top-28"
  >
    <div aria-hidden="true" className="absolute inset-0 opacity-[0.07]">
      <AboutCadPattern testId="founder-cad-pattern" id="founder-cad-grid" />
    </div>
    <span aria-hidden="true" className="absolute left-0 top-0 h-[3px] w-24 bg-[#F47721]" />
    <div className="relative z-10">
      <div className="flex items-center gap-4">
        <motion.span
          variants={{ hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: 0.42, delay: 0.6, ease: EASE } } }}
          className="block h-[2px] w-9 origin-left bg-[#F47721]"
        />
        <motion.p variants={founderItemVariants} custom={0.72} className="font-inter text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F47721]" data-testid="founder-label">
          Founder's Message
        </motion.p>
      </div>

      <h3 className="font-manrope font-bold tracking-tight leading-[1.1] text-[30px] md:text-[34px] mt-7" data-testid="founder-heading">
        <span className="block overflow-hidden">
          <motion.span variants={{ hidden: { opacity: 0, x: -32 }, show: { opacity: 1, x: 0, transition: { duration: 0.68, delay: 1.1, ease: EASE } } }} className="block">
            Building with Purpose.
          </motion.span>
        </span>
        <span className="block overflow-hidden text-[#F47721]">
          <motion.span variants={{ hidden: { opacity: 0, x: -32 }, show: { opacity: 1, x: 0, transition: { duration: 0.68, delay: 1.8, ease: EASE } } }} className="block">
            Delivering with Integrity.
          </motion.span>
        </span>
      </h3>

      <motion.p variants={founderItemVariants} custom={2.5} className="font-inter text-[#DCE5EC] text-[15px] leading-[1.75] mt-7" data-testid="founder-paragraph-1">
        “Every successful project begins with a clear understanding of its purpose, followed by disciplined planning, sound engineering and focused execution. At VR Vision, we are committed to delivering solutions that are practical, reliable and built around the real needs of our clients.”
      </motion.p>
      <motion.p variants={founderItemVariants} custom={3.2} className="font-inter text-[#C8D2DC] text-[15px] leading-[1.75] mt-5" data-testid="founder-paragraph-2">
        “We believe that lasting success is built on technical excellence, transparency and strong partnerships. As we continue to grow, our focus remains on embracing better technologies, strengthening our capabilities and creating meaningful value through every project we undertake.”
      </motion.p>

      <motion.div variants={founderItemVariants} custom={3.8} className="mt-8 border-t border-[rgba(255,255,255,0.12)] pt-6" data-testid="founder-identification">
        <p className="font-manrope font-bold tracking-tight text-lg text-white">GANGULA GOPI KRISHNA</p>
        <p className="font-inter text-[11px] uppercase tracking-[0.16em] leading-relaxed text-[#C8D2DC] mt-2">
          Founder & Director · PMP® · 15+ Years of Engineering & Project Management Experience
        </p>
      </motion.div>
    </div>
  </motion.aside>
);

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative bg-[#F7F7F4] py-24 lg:py-32">
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.img
        src={IMAGES.aboutHero}
        alt="Modern pharmaceutical facility and controlled engineering environment"
        data-testid="about-background"
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ opacity: 0, scale: 1 }}
        whileInView={{ opacity: 1, scale: 1.015 }}
        viewport={VIEWPORT}
        transition={{ opacity: { duration: 0.8, ease: EASE }, scale: { duration: 18, ease: "easeInOut" } }}
      />
      <div className="absolute inset-0 bg-white/[0.82]" />
      <motion.div
        aria-hidden="true"
        className="absolute inset-[-8%] opacity-[0.04] pointer-events-none"
        animate={{ x: [-24, 24, -24], y: [-10, 10, -10] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      >
        <AboutCadPattern />
      </motion.div>
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-[#E9EEF2]/45 pointer-events-none"
          animate={{ x: [-36, 36, -36] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.45, delay: 0.25, ease: EASE }}
            className="flex items-center gap-4"
          >
            <span className="h-[2px] w-10 bg-[#F47721]" />
            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F47721]" data-testid="about-label">
              The Company
            </p>
          </motion.div>
          <h2 className="font-manrope font-bold tracking-tight leading-[1.06] text-[42px] md:text-[52px] lg:text-[56px] mt-6" data-testid="about-heading">
            <HeroLine delay={0.4} className="text-[#13263A]">ENGINEERING EXPERTISE.</HeroLine>
            <HeroLine delay={0.58} className="text-[#F47721]">BUILT AROUND DELIVERY.</HeroLine>
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.55, delay: 0.82, ease: EASE }}
            className="font-inter text-[#4B5560] text-base md:text-[17px] leading-[1.65] mt-6 max-w-2xl"
            data-testid="about-intro"
          >
            Integrated engineering and project delivery for complex industrial, pharmaceutical, laboratory and commercial facilities.
          </motion.p>
        </div>

        <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7 space-y-6">
            {CHAPTERS.map((chapter, i) => (
              <ChapterCard key={chapter.num} chapter={chapter} index={i} />
            ))}
          </div>
          <div className="lg:col-span-5 lg:pb-24">
            <FounderMessage />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
          className="mt-16 lg:mt-20"
          data-testid="industries-section"
        >
          <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.3em] text-[#13263A]" data-testid="industries-label">
            Industries We Serve
          </p>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="industries-list">
            {INDUSTRIES.map((industry) => (
              <span
                key={industry}
                className="rounded-full border border-[#DDE5EB] bg-white/[0.55] px-5 py-2.5 font-inter text-[12px] font-semibold uppercase tracking-[0.12em] text-[#13263A] transition-colors duration-300 hover:border-[#F47721] hover:bg-[#F47721]/10 hover:text-[#13263A]"
              >
                {industry}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
